import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import "./App.css";

// Components
import Sidebar from "./components/sidebar";
import Header from "./components/header";
import Home from "./components/home";
import AuthProvider from "./components/authProvider";
// - Leads
import LeadList from "./components/leads/leadList";
import LeadDetail from "./components/leads/leadDetail";
import CreateLead from "./components/leads/createLead";
// - Clients
import ClientList from "./components/clients/clientList";
import ClientDetail from "./components/clients/clientDetail";
import CreateClient from "./components/clients/createClient";
// - HR
import HROverview from "./components/hr/hrOverview";
// -- Contracts
import ContractsOverview from "./components/hr/contracts/contractsOverview";
// -- Employees
import EmployeeDetail from "./components/hr/employees/employeeDetail";
import CreateEmployee from "./components/hr/employees/createEmployee";
// -- Equipment
import EquipmentOverview from "./components/hr/equipment/equipmentOverview";
// -- Reimbursements
import ReimbursementsOverview from "./components/hr/reimbursements/reimbursementsOverview";
// -- Vacations
import VacationsOverview from "./components/hr/vacations/vacationsOverview";
// -- Calendar
import CalendarOverview from "./components/hr/calendar/calendarOverview";
// - Mail Log
import MailLog from "./components/mail_log/mailLog";

// Services
import { clientService } from "./services/clientService";
import { leadService } from "./services/leadService";
// import { hrService } from "./services/hrService";
import { mailLogService } from "./services/mailLogService";
import { authService } from "./services/authService";
import { employeeService } from "./services/employeeService";

const PREVIEW_EMAILS = [
  "vforsyth@greenlightfinancial.net",
  "aricci@greenlightfinancial.net",
  "dev@greenlightfinancial.net",
];

const RequireMasterAccess = ({ userPermission, isPreviewUser, children }) => {
  const location = useLocation();

  if (!isPreviewUser && userPermission === "restrictedAccess") {
    // fire once per render
    toast.error("You don't have permission to create elements.");
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  return children;
};

const RequireHRManager = ({ currentEmployee, isPreviewUser, children }) => {
  if (!currentEmployee) return null;
  if (!isPreviewUser && currentEmployee.is_hr_manager !== "Yes") {
    return currentEmployee.id ? (
      <Navigate to={`/employee/${currentEmployee.id}`} replace />
    ) : (
      <Navigate to="/" replace />
    );
  }
  return children;
};

const RequireOwnEmployee = ({ currentEmployee, isPreviewUser, children }) => {
  const { id } = useParams();
  if (!currentEmployee) return null;
  const isHRManager = currentEmployee.is_hr_manager === "Yes";
  const isOwnRecord = String(id) === String(currentEmployee.id);
  if (!isPreviewUser && !isHRManager && !isOwnRecord) {
    return currentEmployee.id ? (
      <Navigate to={`/employee/${currentEmployee.id}`} replace />
    ) : (
      <Navigate to="/" replace />
    );
  }
  return children;
};

const RequireManagerLevel = ({ currentEmployee, isPreviewUser, children }) => {
  if (!currentEmployee) return null;
  const isHRManager = currentEmployee.is_hr_manager === "Yes";
  const isManagerOrPartner =
    currentEmployee.level === "Manager" || currentEmployee.level === "Partner";
  if (!isPreviewUser && !isHRManager && !isManagerOrPartner) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const App = () => {
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState(null);
  const [mailLog, setMailLog] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClientType, setSelectedClientType] = useState("all");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userPermission, setUserPermission] = useState(null);
  const [totalClients, setTotalClients] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalMailLog, setTotalMailLog] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [initializing, setInitializing] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const didInit = useRef(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const isPreviewUser = PREVIEW_EMAILS.includes(
    (user?.username ?? "").toLowerCase(),
  );

  const refreshCurrentEmployee = useCallback(async () => {
    const email = user?.username;
    if (!email) return;
    try {
      const res = await employeeService.getEmployees(email, 1, 0);
      const match = res.items?.[0];
      if (match) {
        setCurrentEmployee({
          id: match.id,
          is_hr_manager: match.employee?.is_hr_manager ?? "No",
          level: match.employee?.level ?? "",
        });
      }
    } catch (e) {
      console.error("Failed to refresh current employee", e);
    }
  }, [user]);

  // Load clients
  const loadClients = useCallback(
    async (
      targetPage = 1,
      targetRowsPerPage = 50,
      { silent = false, filters = columnFilters } = {},
    ) => {
      try {
        if (!silent) setListLoading(true);
        const response = await clientService.getClients(
          searchTerm,
          selectedClientType,
          targetRowsPerPage,
          (targetPage - 1) * targetRowsPerPage,
          filters,
        );
        setClients(response.items);
        setTotalClients(response.total);
        setPage(targetPage);
        setRowsPerPage(targetRowsPerPage);
      } catch (error) {
        console.error("Failed to load clients:", error);
      } finally {
        if (!silent) setListLoading(false);
      }
    },
    [searchTerm, selectedClientType, columnFilters],
  );

  // Load leads
  const loadLeads = useCallback(
    async (
      targetPage = 1,
      targetRowsPerPage = 50,
      { silent = false, filters = columnFilters } = {},
    ) => {
      try {
        if (!silent) setListLoading(true);
        const response = await leadService.getLeads(
          searchTerm,
          targetRowsPerPage,
          (targetPage - 1) * targetRowsPerPage,
          filters,
        );
        setLeads(Array.isArray(response.items) ? response.items : []);
        setTotalLeads(response.total);
        setPage(targetPage);
        setRowsPerPage(targetRowsPerPage);
      } catch (error) {
        console.error("Failed to load leads:", error);
      } finally {
        if (!silent) setListLoading(false);
      }
    },
    [searchTerm, columnFilters],
  );

  // Load mail log
  const loadMailLog = useCallback(
    async (
      targetPage = 1,
      targetRowsPerPage = 50,
      { silent = false, filters = columnFilters } = {},
    ) => {
      try {
        if (!silent) setListLoading(true);
        const response = await mailLogService.getMailLog(
          searchTerm,
          targetRowsPerPage,
          (targetPage - 1) * targetRowsPerPage,
          filters,
        );
        setMailLog(response.items);
        setTotalMailLog(response.total);
        setPage(targetPage);
        setRowsPerPage(targetRowsPerPage);
      } catch (error) {
        console.error("Failed to load mail log:", error);
      } finally {
        if (!silent) setListLoading(false);
      }
    },
    [searchTerm, columnFilters],
  );

  // Initialize authentication
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const initAuth = async () => {
      try {
        const authResult = await authService.initialize();
        if (authResult.isAuthenticated) {
          setIsAuthenticated(true);
          setUser(authResult.user);
          setUserPermission(authResult.permission);
          const email = authResult.user?.username;
          if (email) {
            try {
              const res = await employeeService.getEmployees(email, 1, 0);
              const match = res.items?.[0];
              if (match) {
                setCurrentEmployee({
                  id: match.id,
                  is_hr_manager: match.employee?.is_hr_manager ?? "No",
                  level: match.employee?.level ?? "",
                });
              }
            } catch (e) {
              console.error("Failed to load current employee", e);
            }
          }
          await loadClients(1, rowsPerPage);
          await loadLeads(1, rowsPerPage);
          await loadMailLog(1, rowsPerPage);
        }
      } catch (error) {
        console.error("Auth initialization failed:", error);
      } finally {
        setInitializing(false);
      }
    };

    initAuth();
  }, [loadClients, loadLeads, loadMailLog, rowsPerPage]); // run once on mount

  // Handle real-time search
  useEffect(() => {
    if (isAuthenticated) {
      const debounceTimer = setTimeout(() => {
        loadClients(1, rowsPerPage);
        loadLeads(1, rowsPerPage);
        loadMailLog(1, rowsPerPage);
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
  }, [
    searchTerm,
    selectedClientType,
    isAuthenticated,
    rowsPerPage,
    loadClients,
    loadLeads,
    loadMailLog,
  ]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(() => {
      loadClients(page, rowsPerPage, { silent: true });
      loadLeads(page, rowsPerPage, { silent: true });
      loadMailLog(page, rowsPerPage, { silent: true });
    }, 15000);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, page, rowsPerPage, loadClients, loadLeads, loadMailLog]);

  const handleLogin = async () => {
    try {
      const result = await authService.login();
      if (result.isAuthenticated) {
        setIsAuthenticated(true);
        setUser(result.user);
        setUserPermission(result.permission);
        const email = result.user?.username;
        if (email) {
          try {
            const res = await employeeService.getEmployees(email, 1, 0);
            const match = res.items?.[0];
            if (match) {
              setCurrentEmployee({
                id: match.id,
                is_hr_manager: match.employee?.is_hr_manager ?? "No",
                level: match.employee?.level ?? "",
              });
            }
          } catch (e) {
            console.error("Failed to load current employee", e);
          }
        }
        await loadClients(1, rowsPerPage);
        await loadLeads(1, rowsPerPage);
        await loadMailLog(1, rowsPerPage);
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setUserPermission(null);
      setCurrentEmployee(null);
      setClients([]);
      setLeads(null);
      setMailLog([]);
      setTotalClients(0);
      setTotalLeads(0);
      setTotalMailLog(0);
      setPage(1);
      setRowsPerPage(50);
      setListLoading(false);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleClientCreated = (newClient) => {
    setPage(1);
    loadClients(1, rowsPerPage);
  };

  const handleClientUpdated = () => {
    loadClients(page, rowsPerPage);
  };

  const handleClientDeleted = (clientId) => {
    const isLastItemOnPage = clients.length === 1 && page > 1;
    const targetPage = isLastItemOnPage ? page - 1 : page;
    loadClients(targetPage, rowsPerPage);
    setPage(targetPage);
  };

  const handleLeadCreated = (newLead) => {
    setPage(1);
    loadLeads(1, rowsPerPage);
  };

  const handleLeadUpdated = () => {
    loadLeads(page, rowsPerPage);
  };

  const handleLeadDeleted = (leadId) => {
    const currentPageLeadCount = Array.isArray(leads) ? leads.length : 0;
    const isLastItemOnPage = currentPageLeadCount === 1 && page > 1;
    const targetPage = isLastItemOnPage ? page - 1 : page;
    loadLeads(targetPage, rowsPerPage);
    setPage(targetPage);
  };

  const handleMailLogCreated = (newMailLog) => {
    setPage(1);
    loadMailLog(1, rowsPerPage);
  };

  const handleMailLogUpdated = () => {
    loadMailLog(page, rowsPerPage);
  };

  const handleMailLogDeleted = (newMailLog) => {
    const isLastItemOnPage = mailLog.length === 1 && page > 1;
    const targetPage = isLastItemOnPage ? page - 1 : page;
    loadMailLog(targetPage, rowsPerPage);
    setPage(targetPage);
  };

  const handlePageChange = (newPage) => {
    if (newPage === page) return;
    loadClients(newPage, rowsPerPage);
    loadLeads(newPage, rowsPerPage);
    loadMailLog(newPage, rowsPerPage);
  };

  const handleRowsPerPageChange = (newSize) => {
    if (newSize === rowsPerPage) return;
    loadClients(1, newSize);
    loadLeads(1, newSize);
    loadMailLog(1, newSize);
  };

  const handleFiltersChange = (filters, { resetPage } = {}) => {
    setColumnFilters(filters || {});
    const targetPage = resetPage ? 1 : page;
    loadClients(targetPage, rowsPerPage, { filters });
    loadLeads(targetPage, rowsPerPage, { filters });
    loadMailLog(targetPage, rowsPerPage, { filters });
  };

  if (initializing) {
    return (
      <div className="min-h-screen bg-canopy-bg flex items-center justify-center">
        <div className="text-center">
          <div className="canopy-spinner mx-auto mb-4"></div>
          <p className="text-canopy-textMuted">Loading Greenlight App...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthProvider onLogin={handleLogin} />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-canopy-bg">
        <Sidebar
          currentEmployeeId={currentEmployee?.id}
          isHRManager={currentEmployee?.is_hr_manager === "Yes"}
          managerAccess={
            currentEmployee?.is_hr_manager === "Yes" ||
            currentEmployee?.level === "Manager" ||
            currentEmployee?.level === "Partner"
          }
          // canAccessVacations={
          //   currentEmployee?.is_hr_manager === "Yes" ||
          //   currentEmployee?.level === "Manager" ||
          //   currentEmployee?.level === "Partner"
          // }
          isPreviewUser={isPreviewUser}
        />

        <div className="canopy-main-content">
          <Header
            user={user}
            userPermission={userPermission}
            onLogout={handleLogout}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedClientType={selectedClientType}
            onClientTypeChange={setSelectedClientType}
          />

          <main className="p-6">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route
                path="/leads"
                element={
                  <LeadList
                    userPermission={userPermission}
                    leads={leads}
                    loading={listLoading}
                    onLeadDeleted={handleLeadDeleted}
                    searchTerm={searchTerm}
                    totalLeads={totalLeads}
                    currentPage={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onFiltersChange={handleFiltersChange}
                  />
                }
              />
              <Route
                path="/create_lead"
                element={
                  <RequireMasterAccess
                    userPermission={userPermission}
                    isPreviewUser={isPreviewUser}
                  >
                    <CreateLead
                      userPermission={userPermission}
                      onLeadCreated={handleLeadCreated}
                    />
                  </RequireMasterAccess>
                }
              />
              <Route
                path="/lead/:id"
                element={
                  <LeadDetail
                    user={user}
                    userPermission={userPermission}
                    onLeadUpdated={handleLeadUpdated}
                    onLeadDeleted={handleLeadDeleted}
                  />
                }
              />
              <Route
                path="/clients"
                element={
                  <ClientList
                    userPermission={userPermission}
                    clients={clients}
                    loading={listLoading}
                    onClientDeleted={handleClientDeleted}
                    searchTerm={searchTerm}
                    selectedClientType={selectedClientType}
                    totalClients={totalClients}
                    currentPage={page}
                    rowsPerPage={rowsPerPage}
                    onClientTypeChange={setSelectedClientType}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onFiltersChange={handleFiltersChange}
                  />
                }
              />
              <Route
                path="/create_client"
                element={
                  <RequireMasterAccess
                    userPermission={userPermission}
                    isPreviewUser={isPreviewUser}
                  >
                    <CreateClient
                      userPermission={userPermission}
                      onClientCreated={handleClientCreated}
                    />
                  </RequireMasterAccess>
                }
              />
              <Route
                path="/client/:id"
                element={
                  <ClientDetail
                    user={user}
                    userPermission={userPermission}
                    onClientUpdated={handleClientUpdated}
                    onClientDeleted={handleClientDeleted}
                  />
                }
              />
              <Route
                path="/human_resources"
                element={
                  <RequireHRManager
                    currentEmployee={currentEmployee}
                    isPreviewUser={isPreviewUser}
                  >
                    <HROverview userPermission={userPermission} />
                  </RequireHRManager>
                }
              />
              <Route
                path="/contracts"
                element={
                  <RequireMasterAccess
                    userPermission={userPermission}
                    isPreviewUser={isPreviewUser}
                  >
                    <ContractsOverview userPermission={userPermission} />
                  </RequireMasterAccess>
                }
              />
              <Route
                path="/calendars"
                element={
                  <RequireMasterAccess
                    userPermission={userPermission}
                    isPreviewUser={isPreviewUser}
                  >
                    <CalendarOverview userPermission={userPermission} />
                  </RequireMasterAccess>
                }
              />
              <Route
                path="/create_employee"
                element={
                  <RequireHRManager
                    currentEmployee={currentEmployee}
                    isPreviewUser={isPreviewUser}
                  >
                    <CreateEmployee userPermission={userPermission} />
                  </RequireHRManager>
                }
              />
              <Route
                path="/employee/:id"
                element={
                  <RequireOwnEmployee
                    currentEmployee={currentEmployee}
                    isPreviewUser={isPreviewUser}
                  >
                    <EmployeeDetail
                      user={user}
                      userPermission={userPermission}
                      isHRManager={currentEmployee?.is_hr_manager === "Yes"}
                      currentEmployeeId={currentEmployee?.id}
                      onCurrentEmployeeUpdated={refreshCurrentEmployee}
                    />
                  </RequireOwnEmployee>
                }
              />
              <Route
                path="/equipment"
                element={
                  <RequireMasterAccess
                    userPermission={userPermission}
                    isPreviewUser={isPreviewUser}
                  >
                    <EquipmentOverview userPermission={userPermission} />
                  </RequireMasterAccess>
                }
              />
              <Route
                path="/reimbursements"
                element={
                  <ReimbursementsOverview
                    userPermission={userPermission}
                    isPreviewUser={isPreviewUser}
                    currentEmployee={currentEmployee}
                  />
                }
              />
              <Route
                path="/vacations"
                element={
                  <RequireManagerLevel
                    currentEmployee={currentEmployee}
                    isPreviewUser={isPreviewUser}
                  >
                    <VacationsOverview
                      userPermission={userPermission}
                      currentEmployee={currentEmployee}
                    />
                  </RequireManagerLevel>
                }
              />
              <Route
                path="/mail_log"
                element={
                  <MailLog
                    userPermission={userPermission}
                    mailLog={mailLog}
                    loading={listLoading}
                    onMailLogCreated={handleMailLogCreated}
                    onMailLogUpdated={handleMailLogUpdated}
                    onMailLogDeleted={handleMailLogDeleted}
                    searchTerm={searchTerm}
                    totalMailLog={totalMailLog}
                    currentPage={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onFiltersChange={handleFiltersChange}
                  />
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: "#1f2937",
              color: "#fff",
            },
          }}
        />
      </div>
    </Router>
  );
};

export default App;
